import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { UserService } from '../user/user.service';
import { Router } from '@angular/router';

/**
 * Intercettore HTTP che:
 * - Aggiunge automaticamente `idRole` e `idUser` a tutte le richieste (escluse /login e /sign-in).
 * - Aggiunge il token JWT nell'header Authorization se disponibile.
 * - Se il server risponde con un 401 (Unauthorized), rimuove il token e reindirizza a `/sign-in` con `tokenUrl` se presente.
 *
 * @param {HttpRequest<any>} req - La richiesta HTTP originale.
 * @param {HttpHandlerFn} next - Il gestore che inoltra la richiesta alla catena successiva.
 * @returns {Observable<HttpEvent<any>>} - Un Observable con la richiesta eventualmente modificata.
 */
export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {
  const userService = inject(UserService);
  const router = inject(Router);

  const userData = userService.userData;
  const userRole = userData?.idRole || 6;
  const userId = userData?.idUser || 1;

  // Verifica se la richiesta è per gli endpoint di login/sign-in
  // TODO: quando verrà creato l'endpoint reale a BE, da levare /jsons\/mocked-stats\.json$/, /jsons\/mocked-evaluation-sheets\.json$/
  const excludedEndpoints = [/\/sign-in$/, /\/login$/, /jsons\/mocked-stats\.json$/, /jsons\/mocked-evaluation-sheets\.json$/];
  const isExcludedRequest = excludedEndpoints.some(endpoint => endpoint.test(req.url));

  let modifiedReq = req;

  // Aggiunge i parametri idRole e idUser solo se la richiesta NON è per login/sign-in
  if (!isExcludedRequest) {
    const modifiedParams = req.params
      .set('idRole', userRole.toString())
      .set('idUser', userId.toString());

    modifiedReq = req.clone({ params: modifiedParams });
  }

  // Recupera il token dall'utente autenticato
  const token = userData?.token || userData?.jwt || null;
  const tokenUrl = userData?.tokenUrl || null; // Recupera il token per il reindirizzamento

  if (!token) {
    console.warn("⚠️ Nessun token JWT trovato, la richiesta potrebbe essere respinta dal server.");
  }

  // Se il token esiste, aggiunge l'Authorization e altri header
  if (token) {
    modifiedReq = modifiedReq.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        env: 'TEST',
        ...modifiedReq.headers.keys().reduce((acc, key) => {
          acc[key] = modifiedReq.headers.get(key);
          return acc;
        }, {} as { [key: string]: string })
      }
    });
  }

  // Inoltra la richiesta e intercetta gli errori
  return next(modifiedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && token) {
        console.error("❌ Errore 401: Utente non autorizzato, reindirizzamento a /sign-in");

        // Rimuove i dati dell'utente
        userService.clearUserData();

        // Reindirizza alla pagina di login con tokenUrl se presente
        const queryParams = tokenUrl ? { token: tokenUrl } : {};
        router.navigate(['/sign-in'], { queryParams });

      }
      return throwError(() => error);
    })
  );
};
